<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

import { useConfigStore } from '../stores/config'
import { usePageStore } from '../stores/page'
import { useAdaStore } from '../stores/ada'
import { useClient } from '../utils/client-only'
import { useDebounce } from '../composables/use-debounce'
import AdaAdSlot from './ad-slots/AdaAdSlot.vue'
import TeaserGroup from './teaser/TeaserGroup.vue'

import type { BxMbWindow } from '../types'

withDefaults(defineProps<{
  brand?: string
}>(), {
  brand: ''
})

const configStore = useConfigStore()
const pageStore = usePageStore()
const adaStore = useAdaStore()

const { rsConfig } = storeToRefs(configStore)
const { pageData } = storeToRefs(pageStore)
const { blockAds } = storeToRefs(adaStore)

useClient()
const { debounce } = useDebounce()

const rootEl = ref<HTMLElement | null>(null)
const aktiveChunkId = ref(0)

const preloadChunkDistanceMax = 300
const registeredAdSlots: string[] = []
let stickyTeaser: HTMLElement[] = []
let countStickyTeaser = 0
let asideWrapper: HTMLElement | null = null
let asideWrapperHeight = 0
let chunkSwitchScrollDistance = 0
let maxVisibleChunkId = 0
let lastStickyInnerOffsetTop = -1000

const stickyAdSlotList = computed<string[]>(() => {
  let allowedAdSlots = rsConfig.value.adSlotUsage?.dynamicAdSlots?.asideAdSlots ?? []
  // remove native teaser from list if not allowed
  if (pageData.value?.metaData?.disallowNativeTeasers || pageData.value?.metaData?.advertorial) {
    allowedAdSlots = allowedAdSlots.filter(item => !item.includes('teaser'))
  }
  return allowedAdSlots
})

const stickyAdSlotObject = computed(() => {
  return blockAds.value
    ? []
    : stickyAdSlotList.value.map((slot) => {
      const [slotName, slotId] = slot.split('_')
      return {
        slotName,
        slotId: parseInt(slotId)
      }
    })
})

const countChunks = computed(() => stickyAdSlotList.value.length)
const countAsideTeasers = computed(() => pageData.value?.metaData?.asideTeasers?.length || 0)

/**
 * Get References to the sticky teaser elements and count them
 */
const initStickyTeaserGroup = () => {
  stickyTeaser = Array.from(rootEl.value?.querySelectorAll<HTMLElement>('.bx-aside__sticky .bx-teaser-container .bx-teaser') ?? [])
  countStickyTeaser = stickyTeaser.length
  switchStickyTeaserByChunk()
}

/**
 * Switch the sticky teaser based to the aktive chunk
 */
const switchStickyTeaserByChunk = () => {
  // ensure that min and max values of stickyTasers are respected, if the aktiveChunkId is out of bounds
  const setActiveTeaserIndex = Math.max(Math.min(aktiveChunkId.value, countStickyTeaser - 1), 0)
  stickyTeaser.forEach((teaser, index) => {
    teaser.style.display = index === setActiveTeaserIndex ? 'block' : 'none'
  })
}

/**
 * Initialize the sticky aside functionality, check heights, calculate distances and register scroll event
 */
const initAsideSticky = () => {
  asideWrapper = document.querySelector('.bx-aside-wrapper')
  asideWrapperHeight = asideWrapper?.clientHeight ?? 0
  // initial calculation of chunkSwitchScrollDistance and maxVisibleChunkId
  // if there is enough space for all chunks, we will extend the scroll distance to switch the chunks
  const stickyChunkHeight = rsConfig.value.adSlotUsage?.dynamicAdSlots?.stickyChunkHeight ?? 0
  chunkSwitchScrollDistance = Math.floor(
    asideWrapperHeight > stickyChunkHeight * countChunks.value
      ? asideWrapperHeight / countChunks.value
      : stickyChunkHeight
  )
  maxVisibleChunkId = Math.floor(asideWrapperHeight / chunkSwitchScrollDistance) - 1
}

/**
 * Register the ad slot for the molten bundle only once
 */
const registerAdSlotOnce = (adSlot: string) => {
  if (!blockAds.value && adSlot && !registeredAdSlots.includes(adSlot)) {
    const bxWindow = window as unknown as BxMbWindow
    // check if molten bundle is already loaded, if not wait for it
    if (bxWindow.MoltenBundle?.cmd) {
      registeredAdSlots.push(adSlot)
      bxWindow.bxMb.adSlots.push(adSlot)
      bxWindow.MoltenBundle.cmd.push(() => {
        bxWindow.MoltenBundle.push(adSlot)
      })
    } else {
      setTimeout(() => {
        registerAdSlotOnce(adSlot)
      }, 10)
    }
  }
}

/**
 * Check if the height of the aside wrapper or the static container has changed
 */
const hasHeightChanged = () => asideWrapper?.clientHeight !== asideWrapperHeight

/**
 * Handle the scroll event, calculate the aktive chunk, preload adSlots and switch the sticky teaser
 */
const handleScroll = () => {
  // recalculate initial values if the height of the aside wrapper or the static container has changed
  if (hasHeightChanged()) {
    initAsideSticky()
  }

  // calculate the offset of the sticky aside - if the chunk gets stuck, the offset gets positive
  // restict the offset to the available space of the availableChunkRotationHeight/asideWrapperHeight
  const stickyInnerOffsetTop = Math.min(
    -((asideWrapper?.offsetTop ?? 0) + document.body.scrollTop + document.body.getBoundingClientRect().top),
    asideWrapperHeight - (rsConfig.value.adSlotUsage?.dynamicAdSlots?.stickyChunkHeight ?? 0)
  )

  // dont calc further than the last chunk, we reached end of aside wrapper and stay at the last chunk
  const calculatedChunk = Math.min(
    Math.floor(stickyInnerOffsetTop / chunkSwitchScrollDistance),
    countChunks.value - 1
  )

  // check the scroll direction
  const scrollDirection = stickyInnerOffsetTop - lastStickyInnerOffsetTop > 0 ? 'down' : 'up'
  lastStickyInnerOffsetTop = stickyInnerOffsetTop

  // preload the ad slot for the next chunk regarding the scroll direction
  if (calculatedChunk >= 0) {
    const preloadAdSlotIndex = scrollDirection === 'down' ? calculatedChunk + 1 : calculatedChunk - 1
    /**
     * Especially with long articles, where there is enough space to display all chunks
     * and the scroll area for switching chunks (chunkSwitchScrollDistance) increases
     * - based on dividing the available height (availableChunkRotationHeight/asideWrapperHeight) by the number of chunks -
     * we may preload the rotating ad slots far too early.
     *
     * In the frontend test article, which is far from the reality of live articles, we come up with a factor of over 6,
     * i.e. over 3600px that still have to be scrolled before the ad is actually visible. That could then be considered fraud.
     *
     * So we also have to find out how far away the next chunk is in order to trigger the next ad slot based on a fixed pixel value.
     *
     * To get the position within the active chunk, we calculate stickyInnerOffsetTop (current position within availableChunkRotationHeight/asideWrapperHeight)
     * modulo chunkSwitchScrollDistance (the height that each chunk has within the availableChunkRotationHeight/asideWrapperHeight)
     * and get the position within the current chunk as the remainder of the integer division.
     *
     * If the user scrolls down, we get directly the distance to the next chuck switch.
     *
     * For the distance in the other scrolling direction (up), we have to subtract the calculated value from the chunkSwitchScrollDistance.
     *
     * At the end, depending on the scrolling direction, we have the absolute value in pixels that remain until the next chunk change.
     */
    const nextChunkDistance = Math.abs((scrollDirection === 'down' ? chunkSwitchScrollDistance : 0) - Math.round(Math.abs(stickyInnerOffsetTop % chunkSwitchScrollDistance)))
    // and lets check if the next chunkId is not greater than the maxVisibleChunkId and not less than 1 (index 0 is already registered within page load)
    if (nextChunkDistance < preloadChunkDistanceMax && preloadAdSlotIndex < maxVisibleChunkId && preloadAdSlotIndex >= 1) {
      registerAdSlotOnce(stickyAdSlotList.value[preloadAdSlotIndex])
    }
  }

  if (aktiveChunkId.value !== calculatedChunk && calculatedChunk >= 0) {
    // the user made scroll progress und the calculated chunk changed, so we switch the rotationsSlotId
    aktiveChunkId.value = calculatedChunk
    switchStickyTeaserByChunk()

    // ensure the ad slot for this chunk is registered,
    // maybe the user scrolled too fast or in wrong direction
    if (!registeredAdSlots.includes(stickyAdSlotList.value[aktiveChunkId.value])) {
      registerAdSlotOnce(stickyAdSlotList.value[aktiveChunkId.value])
    }
  }
}

const handleScrollDebounced = () => debounce(() => handleScroll(), 100)

onMounted(() => {
  const vw = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
  if (vw >= 1000) {
    registerAdSlotOnce('rectangle_1')
    // registerAdSlotOnce('rectangle_2')
    initStickyTeaserGroup()
    initAsideSticky()
    // initionally check scroll status once at the beginning to ensure,
    // we know about the right scroll position. Maybe the user already scrolled
    // but we have to wait until page is loaded to get the final available height
    window.addEventListener('load', () => handleScroll())
    document.addEventListener('scroll', handleScrollDebounced, { passive: true })
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('scroll', handleScrollDebounced)
})
</script>

<template>
  <section ref="rootEl"
           class="bx-aside">
    <div class="bx-aside__sticky">
      <TeaserGroup v-if="countAsideTeasers"
                   class="bx-js-prevent-slot-marker"
                   teaser-group-type="bx-teaser-container--one-third-width"
                   :teasers="pageData?.metaData?.asideTeasers"
                   :brand="brand" />
      <AdaAdSlot v-for="(adSlot, index) in stickyAdSlotObject"
                 v-show="aktiveChunkId === index"
                 :id="`bx-ada-aside-rotation-${index}`"
                 :key="`sticky-ad-slot-${index}`"
                 class="bx-ada-aside-rotation"
                 :class="{
                   'bx-teaser-container': adSlot.slotName === 'teaser',
                   'bx-teaser-container--one-third-width': adSlot.slotName === 'teaser'
                 }"
                 :slot-name="adSlot.slotName"
                 :slot-id="adSlot.slotId" />
    </div>
  </section>
</template>
